"use client";

import { useState, useEffect } from "react";
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Divider, Drawer,
  Grid, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, TablePagination, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EventNoteIcon from "@mui/icons-material/EventNote";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { useSelector } from "react-redux";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";

const pad = (n) => String(n).padStart(2, "0");
const nowStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function DailyExpenseManager() {
  const token = useSelector((state) => state.loginSlice.token);

  const [expenses, setExpenses]       = useState([]);
  const [totalAmt, setTotalAmt]       = useState(0);
  const [loading, setLoading]         = useState(false);
  const [drawerOpen, setDrawerOpen]   = useState(false);

  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);

  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo]     = useState("");
  const [filterName, setFilterName] = useState("");

  const [form, setForm]       = useState({ name: "", reason: "", amount: "", date: nowStr(), remarks: "" });
  const [saving, setSaving]   = useState(false);
  const [formError, setFormError] = useState("");

  const [pettyBalance, setPettyBalance] = useState(null);
  const [employees, setEmployees] = useState([]);

  const fetchPettyBalance = async () => {
    try {
      const res = await axiosRequest("GET", `${apiBaseUrl}/petty-fund`, token);
      if (res?.data?.status) setPettyBalance(res.data.data.balance ?? 0);
    } catch { /* ignore */ }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axiosRequest("GET", `${apiBaseUrl}/employees`, token);
      const list = Array.isArray(res?.data) ? res.data : [];
      setEmployees(list.filter((e) => e.status !== false));
    } catch { /* ignore */ }
  };

  const fetchExpenses = async (currentPage = page, currentSize = rowsPerPage) => {
    setLoading(true);
    try {
      const params = [`page=${currentPage + 1}`, `size=${currentSize}`];
      if (filterFrom) params.push(`date_from=${new Date(filterFrom).toISOString()}`);
      if (filterTo)   params.push(`date_to=${new Date(filterTo).toISOString()}`);
      if (filterName) params.push(`name=${encodeURIComponent(filterName)}`);
      const res = await axiosRequest("GET", `${apiBaseUrl}/daily-expense?${params.join("&")}`, token);
      if (res?.data?.status) {
        setExpenses(res.data.data.list || []);
        setTotalAmt(res.data.data.total || 0);
        setTotalRecords(res.data.data.pagination?.totalRecords || 0);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchExpenses(); fetchPettyBalance(); fetchEmployees(); }, []);

  const handleSearch = () => { setPage(0); fetchExpenses(0, rowsPerPage); };

  const handleReset = () => {
    setFilterFrom(""); setFilterTo(""); setFilterName("");
    setPage(0);
    setTimeout(() => fetchExpenses(0, rowsPerPage), 0);
  };

  const handleOpenDrawer = () => {
    setForm({ name: "", reason: "", amount: "", date: nowStr(), remarks: "" });
    setFormError("");
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    setFormError("");
    if (!form.name.trim())                        return setFormError("Recipient name is required.");
    if (!form.reason.trim())                      return setFormError("Reason is required.");
    if (!form.amount || Number(form.amount) <= 0) return setFormError("Enter a valid amount.");
    setSaving(true);
    try {
      const res = await axiosRequest("POST", `${apiBaseUrl}/daily-expense`, token, {
        name:    form.name.trim(),
        reason:  form.reason.trim(),
        amount:  Number(form.amount),
        date:    form.date ? new Date(form.date).toISOString() : new Date().toISOString(),
        remarks: form.remarks.trim(),
      });
      if (res?.data?.status === false || res?.status === false) {
        setFormError(res?.data?.message || res?.message || "Failed to save.");
        return;
      }
      setDrawerOpen(false);
      fetchExpenses();
      fetchPettyBalance();
    } catch { setFormError("Something went wrong."); }
    finally { setSaving(false); }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: { xs: 7, md: 12 }, pb: 8 }}>

      {/* PAGE HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <EventNoteIcon sx={{ color: "#e65100", fontSize: 36 }} />
          <Box>
            <Typography variant="h4" fontWeight={700}>Petty Expense Manager</Typography>
            <Typography variant="caption" color="text.secondary">Records daily expenses — does not affect counter or shift ledger</Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="contained" sx={{ bgcolor: "#e65100", "&:hover": { bgcolor: "#bf360c" } }} startIcon={<AddIcon />} onClick={handleOpenDrawer}>
            Add Petty Expense
          </Button>
        </Box>
      </Box>

      {/* PETTY FUND BALANCE CARD (read-only) */}
      <Paper sx={{
        p: 2, mb: 3, borderRadius: 2,
        background: "linear-gradient(135deg,#1b5e20,#388e3c)",
        color: "#fff",
        display: "flex", alignItems: "center", gap: 1.5,
      }}>
        <AccountBalanceWalletIcon sx={{ fontSize: 36, opacity: 0.9 }} />
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>Petty Expense Fund Balance</Typography>
          <Typography variant="h5" fontWeight={800}>
            {pettyBalance !== null ? `${Number(pettyBalance).toLocaleString("en-IN")}` : "—"}
          </Typography>
        </Box>
      </Paper>

      {/* FILTER BAR */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" type="datetime-local" label="From" InputLabelProps={{ shrink: true }} value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" type="datetime-local" label="To" InputLabelProps={{ shrink: true }} value={filterTo} disabled={!filterFrom} inputProps={{ min: filterFrom || "" }} onChange={(e) => setFilterTo(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField fullWidth size="small" label="Recipient Name" placeholder="Search recipient..." value={filterName} onChange={(e) => setFilterName(e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Button fullWidth variant="contained" color="success" onClick={handleSearch}>Search</Button>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Button fullWidth variant="outlined" color="error" onClick={handleReset}>Reset</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* SUMMARY BANNER */}
      <Paper sx={{
        p: 2, mb: 3, borderRadius: 2,
        background: "linear-gradient(135deg,#e65100,#ff6d00)",
        color: "#fff",
        display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2,
      }}>
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>Total Petty Expenses</Typography>
          <Typography variant="h6" fontWeight={700}>{totalRecords} records</Typography>
        </Box>
        <Box textAlign="right">
          <Typography variant="caption" sx={{ opacity: 0.85 }}>Total Amount</Typography>
          <Typography variant="h4" fontWeight={800}>{Number(totalAmt).toLocaleString("en-IN")}</Typography>
        </Box>
      </Paper>

      {/* TABLE */}
      <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#f4f6f8" }}>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Recipient</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Cashier</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>No daily expenses found</TableCell>
                  </TableRow>
                ) : expenses.map((exp, i) => (
                  <TableRow key={exp._id} hover>
                    <TableCell align="center">{page * rowsPerPage + i + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{exp.name}</TableCell>
                    <TableCell>{exp.reason}</TableCell>
                    <TableCell align="center">
                      <Chip label={`${Number(exp.amount).toLocaleString("en-IN")}`} size="small" sx={{ fontWeight: 700, bgcolor: "#ff6d00", color: "#fff" }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{exp.created_by?.name || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary">{exp.created_by?.email || ""}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {exp.date ? new Date(exp.date).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{exp.remarks || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={(_, p) => { setPage(p); fetchExpenses(p, rowsPerPage); }}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { const s = parseInt(e.target.value, 10); setRowsPerPage(s); setPage(0); fetchExpenses(0, s); }}
              rowsPerPageOptions={[10, 20, 50, 100]}
              sx={{ borderTop: "1px solid #e0e0e0" }}
            />
          </>
        )}
      </Paper>

      {/* ADD DRAWER */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 480, p: 3, overflowY: "auto", height: "100%" }}>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <EventNoteIcon sx={{ color: "#e65100" }} />
            <Typography variant="h6" fontWeight={700}>New Petty Expense Entry</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Records daily expenses without affecting the counter or shift ledger
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth size="small"
                options={employees}
                getOptionLabel={(opt) => opt.name || ""}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                value={employees.find((e) => e.name === form.name) || null}
                onChange={(_, newValue) => setForm({ ...form, name: newValue?.name || "" })}
                renderOption={(props, opt) => (
                  <li {...props} key={opt._id}>
                    {opt.name} <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>({opt.designation})</Typography>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Recipient Name *" placeholder="Select employee" />}
                noOptionsText="No employees found — add them in Employee Manager"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Reason *" placeholder="e.g. Tea & Snacks, Stationery, Cleaning" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="datetime-local" label="Date & Time" InputLabelProps={{ shrink: true }} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="number" label="Amount () *" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} inputProps={{ min: 1 }} />
              {pettyBalance !== null && (
                <Alert severity={pettyBalance > 0 ? "info" : "error"} sx={{ mt: 1 }}>
                  Available balance: <b>{Number(pettyBalance).toLocaleString("en-IN")}</b>
                  {pettyBalance <= 0 && " — Add funds via Expense Manager (PETTY EXPENSE type)."}
                </Alert>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" multiline rows={2} label="Remarks (optional)" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
            </Grid>

            {formError && (
              <Grid item xs={12}>
                <Alert severity="error">{formError}</Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={() => setDrawerOpen(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  sx={{ bgcolor: "#e65100", "&:hover": { bgcolor: "#bf360c" } }}
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {saving ? "Saving..." : "Save Expense"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Drawer>
    </Box>
  );
}
